import { createSelector } from 'reselect';
import { goalViewState } from '../../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { LIFE } from '../InsuranceConstants';
import { data as dataSelector } from '../../FinancialPlanSelector';
import { selectedIndex as selectedOwnerIndex } from '../OwnerDropdown/OwnerDropdownSelector';

const def = obj => obj || {};
export const isOpen = (state, props) =>
  !!def(def(goalViewState(state, props).subgoals)[props.subgoalId]).isOpen;
export const isLoading = (state, props) =>
  !!def(def(goalViewState(state, props).subgoals)[props.subgoalId]).noteLoading;
export const insuranceName = (state, props) =>
  `${goalViewState(state, props).selectedSection || LIFE} Insurance`;
export const name = (state, props) => props.note.Name;
export const description = (state, props) =>
  def(def(goalViewState(state, props).subgoals)[props.subgoalId]).noteDescription || '';

const DEFAULT_LIFE_NEEDS_ANALYSIS_TEXT =
  'Pay off all current liabilities, including credit cards and bank loans, with liquid assets to determine the level of uncovered liabilities.';
const DEFAULT_LIFE_INCOME_REPLACEMENT_TEXT = 'Replace future employment income.';
const DEFAULT_DISABILITY_TEXT =
  'At a time when you are unable to work for a living, household expenses may increase while your income decreases. You could potentially deplete funds that might have been saved for your retirement years. The goal of disability insurance is to replace a portion of the after-tax earnings (replacement ratio) of the insured wage earner and to allow you and your family to maintain your current lifestyle.';
const DEFAULT_LONG_TERM_CARE_TEXT =
  '70% of seniors 65 or older will need some form of long-term care (LTC) in their lifetime. Without adequate coverage, the expense of LTC has the potential to deplete savings and strain finances. This graph shows the impacts of a potential LTC event by simulating insurance premiums, benefits, and LTC costs over time.';

const DEFAULT_PRINCIPAL_NOTES = [
  {
    InsuranceType: 0,
    Notes: [
      {
        Name: 'Needs Analysis',
        Description: DEFAULT_LIFE_NEEDS_ANALYSIS_TEXT,
      },
      {
        Name: 'Income Replacement',
        Description: DEFAULT_LIFE_INCOME_REPLACEMENT_TEXT,
      },
    ],
    IsSpouse: false,
  },
  {
    InsuranceType: 1,
    Notes: [
      {
        Name: 'Level 1',
        Description: DEFAULT_DISABILITY_TEXT,
      },
    ],
    IsSpouse: false,
  },
  {
    InsuranceType: 2,
    Notes: [
      {
        Name: 'Level 1',
        Description: DEFAULT_LONG_TERM_CARE_TEXT,
      },
    ],
    IsSpouse: false,
  },
  {
    InsuranceType: 3,
    Notes: [],
    IsSpouse: false,
  },
];

export const insuranceNotes = createSelector(
  selectedOwnerIndex,
  dataSelector,
  (state, props) => props.selectedGoal,
  (state, props) => props.goalId,
  (ownerIndex, data, selectedGoal, goalId) => {
    const isPrincipal = ownerIndex === 0;

    const actionSteps = def(data).PlanActionSteps || [];
    let insuranceNotesArray = actionSteps.filter(as => as.GoalId === goalId && as.Type === 12);
    if (isPrincipal) {
      insuranceNotesArray = insuranceNotesArray.filter(note => !note.IsSpouse);
    } else {
      insuranceNotesArray = insuranceNotesArray.filter(note => note.IsSpouse);
    }

    const insuranceTypeMap = {};
    insuranceNotesArray.forEach((note) => {
      if (!insuranceTypeMap[note.InsuranceType]) {
        insuranceTypeMap[note.InsuranceType] = {
          ClientId: null,
          IsSpouse: !isPrincipal,
          InsuranceType: note.InsuranceType,
          Notes: [],
        };
      }
      insuranceTypeMap[note.InsuranceType].Notes.push({
        Name: note.Name,
        Description: note.Text,
      });
    });
    const defaultFallbackNotes = isPrincipal
      ? DEFAULT_PRINCIPAL_NOTES
      : DEFAULT_PRINCIPAL_NOTES.map(note => ({ ...note, IsSpouse: true }));
    return insuranceNotesArray.length > 0
      ? Object.keys(insuranceTypeMap)
        .sort((a, b) => a - b)
        .map(key => insuranceTypeMap[key])
      : defaultFallbackNotes;
  },
);

export const shouldDrawDescriptionLabel = (state, props) =>
  !name(state, props).match(/^Level \d*$/);
